import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { User, Mail, Linkedin, FileUser, Send, Phone, X, CheckCircle, ArrowLeft } from "lucide-react";
import API_BASE_URL from "../config/api";

const PLANS = [
  {
    id: "starter",
    title: "Starter",
    original: 1599,
    price: 499,
    description: "For students and freshers",
    features: [
      "Profile review & audit",
      "LinkedIn optimization",
      "1 resume feedback",
      "1:1 consultation call",
      "2-3 Job Referrals",
    ],
  },
  {
    id: "professional",
    title: "Professional",
    original: 2599,
    price: 999,
    description: "For working professionals",
    features: [
      "Full profile rewrite",
      "ATS-optimized resume",
      "Recruiter targeting",
      "1:1 consultation call",
      "3-6 Job Referrals",
    ],
    popular: true,
  },
  {
    id: "elite",
    title: "Elite",
    original: 2999,
    price: 1499,
    description: "For senior roles",
    features: [
      "LinkedIn + Resume redesign",
      "Cover Letter + Bio",
      "Strategy session",
      "5-10 Job Referrals",
      "Lifetime support",
    ],
  },
];

const UPI_ID = "shitlasingharjun-5@okicici";
const UPI_QR_URL = "https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=upi://pay?pa=shitlasingharjun-5@okicici%26pn=Arjun%20Singh%26cu=INR";

export default function ContactForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    linkedin: "",
    naukri: "",
  });
  const [loading, setLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [step, setStep] = useState("plans"); // plans | qr

  async function saveSubmission(paymentStatus, planTitle, planPrice) {
    try {
      await fetch(`${API_BASE_URL}/api/submissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          linkedin: form.linkedin,
          naukri: form.naukri,
          source: 'contact_form',
          paymentStatus: paymentStatus,
          plan: planTitle,
          amount: planPrice
        })
      });
    } catch (err) {
      console.error('Failed to save submission');
    }
  }

  function redirectToWhatsApp(paymentStatus, plan) {
    const paymentText = paymentStatus === 'paid' 
      ? `✅ Paid ₹${plan?.price || 0} (${plan?.title || ''})` 
      : paymentStatus === 'pay_later' 
        ? `⏳ Pay Later - ${plan?.title || ''} (₹${plan?.price || 0})`
        : '🆓 Free Review Request';
    
    const message = `
New Profile Audit Request:
---------------------------
👤 Name: ${form.name}
📧 Email: ${form.email}
📱 Phone: ${form.phone}
🔗 LinkedIn: ${form.linkedin || "Not provided"}
📄 Naukri: ${form.naukri || "Not provided"}
💳 Payment: ${paymentText}
    `;
    const encodedMessage = encodeURIComponent(message);
    const whatsappPhone = "917057051648";
    window.location.href = `https://wa.me/${whatsappPhone}?text=${encodedMessage}`;
  }

  async function handleFreeReview(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      alert("Please fill in all required fields");
      return;
    }
    setLoading(true);
    await saveSubmission('free_review', null, 0);
    redirectToWhatsApp('free_review', null);
    setLoading(false);
  }

  function handleBookCall(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      alert("Please fill in all required fields");
      return;
    }
    setShowPaymentModal(true);
    setStep("plans");
    setSelectedPlan(null);
  }

  function handleSelectPlan(plan) {
    setSelectedPlan(plan);
    setStep("qr");
  }

  async function handlePaid() {
    setLoading(true);
    await saveSubmission('paid', selectedPlan.title, selectedPlan.price);
    redirectToWhatsApp('paid', selectedPlan);
    setLoading(false);
  }

  async function handlePayLater() {
    setLoading(true);
    await saveSubmission('pay_later', selectedPlan.title, selectedPlan.price);
    redirectToWhatsApp('pay_later', selectedPlan);
    setLoading(false);
  }

  function closeModal() {
    setShowPaymentModal(false);
    setSelectedPlan(null);
    setStep("plans");
  }

  const inputStyle =
    "flex items-center gap-2 bg-white/10 border border-white/20 rounded-xl px-4 py-3 backdrop-blur-md text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition";

  return (
    <>
      <motion.section
        id="contact"
        className="relative py-24 bg-gradient-to-br from-indigo-900 via-indigo-800 to-indigo-700 text-white overflow-hidden"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.08),transparent)] pointer-events-none" />

        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <motion.h2
            className="text-4xl md:text-5xl font-extrabold mb-4 leading-tight"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            Ready to stand out in your next interview?
          </motion.h2>

          <motion.p
            className="text-indigo-200 text-lg mb-10 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            Request a free profile audit and discover what recruiters really want
            to see — delivered straight to your inbox.
          </motion.p>

          <motion.form
            onSubmit={(e) => e.preventDefault()}
            className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-8 shadow-xl space-y-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <div className="grid md:grid-cols-2 gap-4">
              <div className={inputStyle}>
                <User size={18} />
                <input
                  required
                  placeholder="Full Name *"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-transparent w-full outline-none"
                />
              </div>

              <div className={inputStyle}>
                <Mail size={18} />
                <input
                  required
                  type="email"
                  placeholder="Email Address *"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-transparent w-full outline-none"
                />
              </div>
            </div>

            <div className={inputStyle}>
              <Phone size={18} />
              <input
                required
                type="tel"
                placeholder="Mobile Number *"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="bg-transparent w-full outline-none"
              />
            </div>

            <div className={inputStyle}>
              <Linkedin size={18} />
              <input
                placeholder="LinkedIn Profile URL"
                value={form.linkedin}
                onChange={(e) => setForm({ ...form, linkedin: e.target.value })}
                className="bg-transparent w-full outline-none"
              />
            </div>

            <div className={inputStyle}>
              <FileUser size={18} />
              <input
                placeholder="Naukri Profile URL (optional)"
                value={form.naukri}
                onChange={(e) => setForm({ ...form, naukri: e.target.value })}
                className="bg-transparent w-full outline-none"
              />
            </div>

            <div className="flex flex-col md:flex-row gap-4 mt-6 justify-center">
              <button
                type="button"
                onClick={handleFreeReview}
                disabled={loading}
                className="bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 px-6 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition transform hover:scale-105 shadow-lg disabled:opacity-50"
              >
                <Send size={18} />
                {loading ? "Sending..." : "Get Free Review"}
              </button>

              <button
                type="button"
                onClick={handleBookCall}
                disabled={loading}
                className="border border-indigo-400 hover:bg-indigo-500/10 px-6 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition transform hover:scale-105"
              >
                <Phone size={18} />
                Book a Call
              </button>
            </div>
          </motion.form>
        </div>
      </motion.section>

      {/* Payment Modal */}
      <AnimatePresence>
        {showPaymentModal && (
          <motion.div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {step === "plans" ? (
                <>
                  <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="text-2xl font-bold">Choose Your Plan</h3>
                        <p className="mt-1 text-indigo-100">Select a plan to proceed with booking</p>
                      </div>
                      <button
                        onClick={closeModal}
                        className="p-2 hover:bg-white/20 rounded-full transition"
                      >
                        <X size={24} />
                      </button>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="grid gap-4">
                      {PLANS.map((plan) => (
                        <div
                          key={plan.id}
                          onClick={() => handleSelectPlan(plan)}
                          className={`relative border-2 rounded-xl p-4 cursor-pointer transition-all hover:shadow-lg ${
                            plan.popular 
                              ? 'border-indigo-500 bg-indigo-50' 
                              : 'border-gray-200 hover:border-indigo-300'
                          }`}
                        >
                          {plan.popular && (
                            <span className="absolute -top-3 left-4 bg-indigo-600 text-white text-xs px-3 py-1 rounded-full font-semibold">
                              Most Popular
                            </span>
                          )}
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h4 className="text-lg font-bold text-gray-900">{plan.title}</h4>
                              <p className="text-sm text-gray-500">{plan.description}</p>
                              <div className="mt-2 flex flex-wrap gap-2">
                                {plan.features.slice(0, 3).map((f, i) => (
                                  <span key={i} className="inline-flex items-center gap-1 text-xs text-gray-600">
                                    <CheckCircle size={12} className="text-green-500" />
                                    {f}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <div className="text-right ml-4">
                              <p className="text-sm text-gray-400 line-through">₹{plan.original}</p>
                              <p className="text-2xl font-bold text-indigo-600">₹{plan.price}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 text-white">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="text-2xl font-bold">Complete Payment</h3>
                        <p className="mt-1 text-green-100">{selectedPlan?.title} Plan - ₹{selectedPlan?.price}</p>
                      </div>
                      <button
                        onClick={closeModal}
                        className="p-2 hover:bg-white/20 rounded-full transition"
                      >
                        <X size={24} />
                      </button>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="bg-gray-50 rounded-xl p-6 flex flex-col items-center">
                      {/* QR Code */}
                      <div className="bg-white p-4 rounded-xl shadow-lg border-2 border-gray-100">
                        <img 
                          src={`${UPI_QR_URL}%26am=${selectedPlan?.price}`}
                          alt="UPI QR Code"
                          className="w-48 h-48"
                        />
                      </div>
                      
                      <div className="mt-4 text-center">
                        <p className="text-sm text-gray-500">Scan with any UPI app</p>
                        <p className="text-xs text-gray-400 mt-1">GPay • PhonePe • Paytm • BHIM</p>
                      </div>

                      <div className="mt-4 bg-white rounded-lg p-3 w-full max-w-xs">
                        <p className="text-xs text-gray-500 text-center">UPI ID</p>
                        <p className="font-mono text-indigo-600 font-semibold text-center text-sm mt-1">
                          {UPI_ID}
                        </p>
                      </div>

                      <div className="mt-3 text-center">
                        <p className="text-xs text-gray-500">Pay to</p>
                        <p className="font-semibold text-gray-800">Arjun Singh</p>
                      </div>

                      <div className="mt-3 bg-green-100 text-green-700 px-6 py-2 rounded-full font-bold text-xl">
                        ₹{selectedPlan?.price}
                      </div>
                    </div>

                    <div className="mt-6 space-y-3">
                      <button
                        onClick={handlePaid}
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-3 hover:from-green-600 hover:to-emerald-700 transition transform hover:scale-[1.02] disabled:opacity-50"
                      >
                        {loading ? "Confirming..." : "✓ I have Paid"}
                      </button>

                      <button
                        onClick={handlePayLater}
                        disabled={loading}
                        className="w-full bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-200 transition disabled:opacity-50"
                      >
                        {loading ? "Processing..." : "Pay Later"}
                      </button>

                      <button
                        onClick={() => setStep("plans")}
                        className="w-full text-gray-500 py-2 text-sm hover:text-gray-700 transition flex items-center justify-center gap-2"
                      >
                        <ArrowLeft size={16} />
                        Change Plan
                      </button>
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
